import { useState } from 'react';
import { TextField, Stack, Typography } from '@mui/material';

type Props = {
  onSubmit?: (code: string) => void;
  onChange?: (code: string) => void;
  error?: string | null;
  hideTitle?: boolean;
};

// ----------------------------------------------------------------------
export default function VerificationCodeInput({ onSubmit, onChange, error, hideTitle }: Props) {
  const [code, setCode] = useState(['', '', '', '']);

  const handlePaste = (
    startIndex: number,
    event: React.ClipboardEvent<HTMLInputElement | HTMLDivElement>
  ) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const digits = pastedText.replace(/\D/g, '');
    if (!digits) return;

    const newCode = [...code];
    let writeIndex = startIndex;

    for (let i = 0; i < digits.length && writeIndex < newCode.length; i += 1) {
      newCode[writeIndex] = digits[i];
      writeIndex += 1;
    }

    setCode(newCode);
    onChange?.(newCode.join(''));

    const filled = newCode.join('');
    if (filled.length === newCode.length) {
      onSubmit?.(filled);
      return;
    }

    const nextEmptyIndex = newCode.findIndex((d) => d === '');
    const focusIndex = nextEmptyIndex === -1 ? newCode.length - 1 : nextEmptyIndex;
    const nextInput = document.getElementById(`code-input-${focusIndex}`);
    if (nextInput) {
      nextInput.focus();
    }
  };

  const handleChange = (
    index: number,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = event.target.value.replace(/\D/g, ''); // Оставляем только цифры
    if (value.length > 1) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    onChange?.(newCode.join(''));

    if (value && index < 3) {
      const nextInput = document.getElementById(`code-input-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }

    if (newCode.join('').length === 4) {
      onSubmit?.(newCode.join(''));
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Backspace' && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-input-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  return (
    <Stack spacing={2}>
      {!hideTitle && <Typography variant="subtitle1">Введите четырехзначный код</Typography>}
      <Stack direction="row" spacing={1}>
        {code.map((digit, index) => (
          <TextField
            key={index}
            id={`code-input-${index}`}
            value={digit}
            onChange={(event) => handleChange(index, event)}
            onPaste={(event) => handlePaste(index, event)}
            onKeyDown={(event) => handleKeyDown(index, event)}
            inputProps={{ maxLength: 1, inputMode: 'numeric', pattern: '[0-9]*' }}
            sx={{ textAlign: 'center' }}
          />
        ))}
      </Stack>
      {error && <Typography color="error">{error}</Typography>}
    </Stack>
  );
}
